import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Megaphone, Package, Plus, Trash2, Copy, Check, ExternalLink,
  MessageCircle, Phone, Send, Eye, EyeOff, ImagePlus, Inbox
} from "lucide-react";
import { toast } from "sonner";

const emptyManual = {
  name: "",
  description: "",
  quantity: "",
  price: "",
  currency: "ETB",
  show_price: true
};

const digits = (value) => (value || "").replace(/[^0-9]/g, "");

const AdvertisePage = () => {
  const { API, authHeader } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingManual, setAddingManual] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [form, setForm] = useState({
    title: "",
    message: "",
    business_name: "Goldspot Trade",
    whatsapp: "",
    phone: ""
  });
  const [products, setProducts] = useState([]);
  const [manual, setManual] = useState(emptyManual);
  const [manualPhoto, setManualPhoto] = useState(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [expandedAd, setExpandedAd] = useState(null);
  const [inquiries, setInquiries] = useState({});
  const [loadingInquiries, setLoadingInquiries] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/trade/shipments`, { headers: authHeader() }),
      axios.get(`${API}/trade/brokers`, { headers: authHeader() }),
      axios.get(`${API}/trade/advertisements`, { headers: authHeader() })
    ]).then(([s, b, a]) => {
      setShipments(s.data);
      setBrokers(b.data);
      setAds(a.data);
    }).catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const selectedShipment = shipments.find(s => s.id === selectedShipmentId);
  const shareUrl = (ad) => `${window.location.origin}/advertise/${ad.share_id}`;
  const shareMessage = (ad) => `${ad.business_name || "Goldspot Trade"} — ${ad.title}\n\n${ad.message || "Goods available for sale."}\n\nView products: ${shareUrl(ad)}`;

  const addShipmentProduct = (item) => {
    if (!selectedShipment) return;
    if (products.some(p => p.source_id === item.id)) {
      toast.error("This goods item is already added");
      return;
    }
    setProducts(prev => [...prev, {
      local_id: crypto.randomUUID(),
      source_id: item.id,
      source: "shipment_item",
      name: item.name,
      description: `Available from ${selectedShipment.shipment_name}`,
      quantity: item.quantity ? String(item.quantity) : "",
      price: "",
      currency: "ETB",
      show_price: true,
      image_path: item.image_path || "",
      image_content_type: item.content_type || "",
      shipment_name: selectedShipment.shipment_name
    }]);
  };

  const updateProduct = (localId, patch) => {
    setProducts(prev => prev.map(p => p.local_id === localId ? { ...p, ...patch } : p));
  };

  const addManualProduct = async () => {
    if (!manual.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setAddingManual(true);
    try {
      let image = { image_path: "", image_content_type: "" };
      if (manualPhoto) {
        const fd = new FormData();
        fd.append("file", manualPhoto);
        const res = await axios.post(`${API}/trade/advertisements/product-image`, fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
        });
        image = res.data;
      }
      setProducts(prev => [...prev, {
        local_id: crypto.randomUUID(),
        source_id: "",
        source: "manual",
        name: manual.name.trim(),
        description: manual.description.trim(),
        quantity: manual.quantity.trim(),
        price: manual.price,
        currency: manual.currency,
        show_price: manual.show_price,
        shipment_name: "",
        ...image
      }]);
      setManual(emptyManual);
      setManualPhoto(null);
      setPhotoInputKey(k => k + 1);
      toast.success("Product added to this advertisement");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the product");
    } finally {
      setAddingManual(false);
    }
  };

  const saveAd = async () => {
    if (!form.title.trim()) {
      toast.error("Advertisement title is required");
      return;
    }
    if (!form.whatsapp.trim() && !form.phone.trim()) {
      toast.error("Add your WhatsApp or phone number");
      return;
    }
    if (products.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        products: products.map(p => ({
          name: p.name,
          description: p.description || "",
          quantity: p.quantity || "",
          price: p.price === "" || p.price === null ? null : Number(p.price),
          currency: p.currency || "ETB",
          show_price: !!p.show_price,
          image_path: p.image_path || "",
          image_content_type: p.image_content_type || "",
          source: p.source || "manual",
          shipment_name: p.shipment_name || ""
        }))
      };
      const res = await axios.post(`${API}/trade/advertisements`, payload, { headers: authHeader() });
      setAds(prev => [{ ...res.data, inquiry_count: 0, new_inquiry_count: 0 }, ...prev]);
      setForm({ title: "", message: "", business_name: "Goldspot Trade", whatsapp: form.whatsapp, phone: form.phone });
      setProducts([]);
      setSelectedShipmentId("");
      toast.success("Advertisement ready — share the link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save advertisement");
    } finally {
      setSaving(false);
    }
  };

  const copyAd = async (ad) => {
    try {
      await navigator.clipboard.writeText(shareMessage(ad));
      setCopiedId(ad.id);
      setTimeout(() => setCopiedId(null), 1800);
      toast.success("Advertisement link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const openWhatsAppShare = (ad) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage(ad))}`, "_blank", "noopener,noreferrer");
  };

  const textBrokers = (ad) => {
    const phones = brokers.filter(b => (b.broker_type || "sales") === "sales" && b.phone).map(b => b.phone);
    if (!phones.length) {
      toast.error("No sales brokers with phone numbers saved");
      return;
    }
    window.location.href = `sms:${phones.join(",")}?&body=${encodeURIComponent(shareMessage(ad))}`;
  };

  const toggleInquiries = async (ad) => {
    if (expandedAd === ad.id) {
      setExpandedAd(null);
      return;
    }
    setExpandedAd(ad.id);
    setLoadingInquiries(ad.id);
    try {
      const res = await axios.get(`${API}/trade/advertisements/${ad.id}/inquiries`, { headers: authHeader() });
      setInquiries(prev => ({ ...prev, [ad.id]: res.data }));
    } catch {
      toast.error("Could not load inquiries");
    } finally {
      setLoadingInquiries(null);
    }
  };

  const markRead = async (adId, inquiryId) => {
    try {
      await axios.patch(`${API}/trade/advertisements/${adId}/inquiries/${inquiryId}/read`, {}, { headers: authHeader() });
      setInquiries(prev => ({
        ...prev,
        [adId]: (prev[adId] || []).map(i => i.id === inquiryId ? { ...i, status: "read" } : i)
      }));
      setAds(prev => prev.map(a => a.id === adId ? { ...a, new_inquiry_count: Math.max((a.new_inquiry_count || 0) - 1, 0) } : a));
    } catch {
      toast.error("Could not mark inquiry as read");
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm("Delete this advertisement and its inquiries?")) return;
    try {
      await axios.delete(`${API}/trade/advertisements/${adId}`, { headers: authHeader() });
      setAds(prev => prev.filter(a => a.id !== adId));
      toast.success("Advertisement deleted");
    } catch {
      toast.error("Could not delete advertisement");
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Sell Goods</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Advertise</h1>
          <p className="text-white/50 mt-2">Create one product link for brokers or buyers — with photos, prices, WhatsApp and inquiries</p>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          {/* Create advertisement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="xl:col-span-3 trade-card rounded-sm p-6"
            data-testid="advertise-create-card"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                <Megaphone className="w-5 h-5 trade-gold" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-serif font-semibold text-xl">New Advertisement</h2>
                <p className="text-xs text-white/45">Add goods from a shipment or create products manually</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="md:col-span-2">
                <Label className="text-xs text-white/60">Advertisement title *</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Excavator, shoes, electronics — goods available"
                  className="trade-input mt-1 h-11 rounded-sm"
                  data-testid="ad-title-input"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">Business name</Label>
                <Input
                  value={form.business_name}
                  onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                  className="trade-input mt-1 h-11 rounded-sm"
                  data-testid="ad-business-input"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">WhatsApp number *</Label>
                <Input
                  value={form.whatsapp}
                  onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                  placeholder="+251..."
                  className="trade-input mt-1 h-11 rounded-sm"
                  data-testid="ad-whatsapp-input"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">Call number</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+251..."
                  className="trade-input mt-1 h-11 rounded-sm"
                  data-testid="ad-phone-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-white/60">Message for buyers</Label>
                <Textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={3}
                  placeholder="Tell brokers what is available, where it is, and any important selling terms..."
                  className="trade-input mt-1 rounded-sm"
                  data-testid="ad-message-input"
                />
              </div>
            </div>

            {/* Shipment goods picker */}
            <div className="border border-white/10 rounded-sm p-4 mb-5">
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-4">Pick Goods From Shipments</h3>
              <Select value={selectedShipmentId} onValueChange={setSelectedShipmentId}>
                <SelectTrigger className="trade-input h-11 rounded-sm" data-testid="ad-shipment-select">
                  <SelectValue placeholder="Choose a shipment with goods" />
                </SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10 max-h-72">
                  {shipments.filter(s => (s.items || []).length > 0).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.shipment_name} ({(s.items || []).length} goods)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedShipment && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {(selectedShipment.items || []).map(item => {
                    const added = products.some(p => p.source_id === item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => addShipmentProduct(item)}
                        disabled={added}
                        className={`text-left p-3 rounded-sm border transition-colors ${
                          added ? "border-[#10b981]/30 bg-[#10b981]/5 text-white/40" : "border-white/10 bg-white/5 hover:border-[#D4AF37]/40"
                        }`}
                        data-testid={`ad-goods-${item.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-sm truncate">{item.name}</span>
                          {added ? <Check size={14} className="trade-green" /> : <Plus size={14} className="trade-gold" />}
                        </div>
                        <p className="text-[11px] text-white/45 mt-1">
                          {item.quantity || 0} pcs · cost ${Number(item.unit_price_usd || 0).toLocaleString()}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Manual product */}
            <div className="border border-white/10 rounded-sm p-4 mb-5">
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-4">Add Manual Product</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Product name *</Label>
                  <Input
                    value={manual.name}
                    onChange={e => setManual(m => ({ ...m, name: e.target.value }))}
                    className="trade-input mt-1 h-11 rounded-sm"
                    data-testid="manual-product-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Quantity</Label>
                  <Input
                    value={manual.quantity}
                    onChange={e => setManual(m => ({ ...m, quantity: e.target.value }))}
                    placeholder="10 pcs, 2 containers..."
                    className="trade-input mt-1 h-11 rounded-sm"
                    data-testid="manual-product-quantity"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Selling price</Label>
                  <Input
                    type="number"
                    min="0"
                    value={manual.price}
                    onChange={e => setManual(m => ({ ...m, price: e.target.value }))}
                    className="trade-input mt-1 h-11 rounded-sm"
                    data-testid="manual-product-price"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Currency</Label>
                  <Select value={manual.currency} onValueChange={v => setManual(m => ({ ...m, currency: v }))}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="manual-product-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="ETB">ETB</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="RMB">RMB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-white/60">Description</Label>
                  <Textarea
                    value={manual.description}
                    onChange={e => setManual(m => ({ ...m, description: e.target.value }))}
                    rows={2}
                    className="trade-input mt-1 rounded-sm"
                    data-testid="manual-product-description"
                  />
                </div>
                <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 cursor-pointer" data-testid="manual-product-photo-label">
                    <ImagePlus size={14} className="trade-gold" />
                    {manualPhoto ? manualPhoto.name : "Add product photo"}
                    <input
                      key={photoInputKey}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => setManualPhoto(e.target.files?.[0] || null)}
                      data-testid="manual-product-photo"
                    />
                  </label>
                  <button
                    onClick={() => setManual(m => ({ ...m, show_price: !m.show_price }))}
                    className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10"
                    data-testid="manual-price-visibility"
                  >
                    {manual.show_price ? <Eye size={14} className="trade-green" /> : <EyeOff size={14} className="trade-gold" />}
                    {manual.show_price ? "Show price" : "Contact for price"}
                  </button>
                  <Button
                    onClick={addManualProduct}
                    disabled={addingManual}
                    className="bg-white/10 text-white hover:bg-white/20 rounded-sm h-10 px-4 ml-auto"
                    data-testid="add-manual-product-btn"
                  >
                    <Plus size={14} className="mr-2" />
                    {addingManual ? "Adding..." : "Add Product"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Selected products */}
            <div className="mb-6">
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-4">Products In This Ad ({products.length})</h3>
              {products.length === 0 ? (
                <div className="text-center p-8 border border-dashed border-white/10 rounded-sm text-white/40 text-sm" data-testid="ad-products-empty">
                  Pick shipment goods or add a manual product
                </div>
              ) : (
                <div className="space-y-3">
                  {products.map(p => (
                    <div key={p.local_id} className="p-4 bg-white/5 rounded-sm border border-white/10" data-testid={`ad-product-${p.local_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{p.name}</p>
                          <p className="text-[11px] text-white/40 truncate">
                            {p.source === "shipment_item" ? `From ${p.shipment_name}` : "Manual product"}
                            {p.quantity ? ` · ${p.quantity}` : ""}
                          </p>
                        </div>
                        <button onClick={() => setProducts(prev => prev.filter(x => x.local_id !== p.local_id))} className="p-2 text-red-400/70 hover:text-red-400" data-testid={`remove-ad-product-${p.local_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Input
                          type="number"
                          min="0"
                          value={p.price}
                          onChange={e => updateProduct(p.local_id, { price: e.target.value })}
                          placeholder="Price"
                          className="trade-input h-10 rounded-sm text-sm"
                          data-testid={`ad-product-price-${p.local_id}`}
                        />
                        <Select value={p.currency} onValueChange={v => updateProduct(p.local_id, { currency: v })}>
                          <SelectTrigger className="trade-input h-10 rounded-sm text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#142039] border-white/10">
                            <SelectItem value="ETB">ETB</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="RMB">RMB</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => updateProduct(p.local_id, { show_price: !p.show_price })}
                          className="col-span-2 flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10"
                          data-testid={`ad-product-visibility-${p.local_id}`}
                        >
                          {p.show_price ? <Eye size={13} className="trade-green" /> : <EyeOff size={13} className="trade-gold" />}
                          {p.show_price ? "Show price" : "Contact for price"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={saveAd}
              disabled={saving}
              className="trade-btn-primary rounded-sm h-12 px-8 w-full md:w-auto"
              data-testid="save-ad-btn"
            >
              <Megaphone size={16} className="mr-2" />
              {saving ? "Creating..." : "Create Advertisement"}
            </Button>
          </motion.div>

          {/* Saved advertisements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            className="xl:col-span-2"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm uppercase tracking-widest trade-gold">Ready To Share</h2>
              <span className="text-xs text-white/40">{ads.length} ad{ads.length === 1 ? "" : "s"}</span>
            </div>
            {loading ? (
              <div className="trade-card rounded-sm p-10 text-center text-white/40">Loading...</div>
            ) : ads.length === 0 ? (
              <div className="trade-card rounded-sm p-10 text-center">
                <Package className="w-14 h-14 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
                <h3 className="text-xl font-serif mb-2">No advertisements yet</h3>
                <p className="text-white/50 text-sm">Create your first goods advertisement on the left</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ads.map(ad => (
                  <div key={ad.id} className="trade-card rounded-sm p-5" data-testid={`ad-card-${ad.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <h3 className="font-serif font-semibold text-lg truncate">{ad.title}</h3>
                        <p className="text-xs text-white/45">
                          {ad.business_name} · {(ad.products || []).length} product{(ad.products || []).length === 1 ? "" : "s"}
                        </p>
                      </div>
                      {(ad.new_inquiry_count || 0) > 0 && (
                        <span className="px-2 py-1 text-[10px] rounded-sm bg-[#10b981]/10 trade-green border border-[#10b981]/20" data-testid={`ad-new-inquiries-${ad.id}`}>
                          {ad.new_inquiry_count} new
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(ad.products || []).slice(0, 3).map(p => (
                        <span key={p.id} className="px-2 py-1 text-[10px] rounded-sm bg-white/5 text-white/60">{p.name}</span>
                      ))}
                      {(ad.products || []).length > 3 && <span className="px-2 py-1 text-[10px] rounded-sm bg-white/5 text-white/60">+{ad.products.length - 3} more</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => copyAd(ad)} className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20" data-testid={`copy-ad-${ad.id}`}>
                        {copiedId === ad.id ? <Check size={12} /> : <Copy size={12} />} Copy
                      </button>
                      <a href={shareUrl(ad)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10" data-testid={`open-ad-${ad.id}`}>
                        <ExternalLink size={12} /> Open
                      </a>
                      <button onClick={() => openWhatsAppShare(ad)} className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20" data-testid={`whatsapp-ad-${ad.id}`}>
                        <MessageCircle size={12} /> WhatsApp
                      </button>
                      <button onClick={() => textBrokers(ad)} className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10" data-testid={`text-brokers-ad-${ad.id}`}>
                        <Send size={12} /> Text Brokers
                      </button>
                      <button onClick={() => toggleInquiries(ad)} className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10" data-testid={`ad-inquiries-${ad.id}`}>
                        <Inbox size={12} /> Inquiries
                      </button>
                      <button onClick={() => deleteAd(ad.id)} className="p-2 text-red-400/70 hover:text-red-400" data-testid={`delete-ad-${ad.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {expandedAd === ad.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-4 border-t border-white/5" data-testid={`ad-inquiry-panel-${ad.id}`}>
                            {loadingInquiries === ad.id ? (
                              <p className="text-sm text-white/40">Loading inquiries...</p>
                            ) : (inquiries[ad.id] || []).length === 0 ? (
                              <p className="text-sm text-white/40">No buyer inquiries yet</p>
                            ) : (
                              <div className="space-y-3">
                                {(inquiries[ad.id] || []).map(i => (
                                  <div key={i.id} className="p-3 bg-white/5 rounded-sm" data-testid={`inquiry-${i.id}`}>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                      <div>
                                        <p className="text-sm font-semibold">{i.buyer_name}</p>
                                        <p className="text-[11px] text-white/40">{i.buyer_phone}{i.product_name ? ` · ${i.product_name}` : ""}</p>
                                      </div>
                                      {i.status === "new" && <span className="text-[10px] trade-green uppercase tracking-widest">New</span>}
                                    </div>
                                    {i.message && <p className="text-xs text-white/65 whitespace-pre-wrap mb-3">{i.message}</p>}
                                    <div className="flex flex-wrap gap-2">
                                      <a href={`tel:${i.buyer_phone}`} className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] rounded-sm bg-white/5 text-white/70">
                                        <Phone size={11} /> Call
                                      </a>
                                      <a href={`https://wa.me/${digits(i.buyer_phone)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] rounded-sm bg-[#10b981]/10 trade-green">
                                        <MessageCircle size={11} /> WhatsApp
                                      </a>
                                      {i.status === "new" && (
                                        <button onClick={() => markRead(ad.id, i.id)} className="px-2.5 py-1.5 text-[11px] rounded-sm bg-[#D4AF37]/10 trade-gold" data-testid={`mark-inquiry-read-${i.id}`}>
                                          Mark Read
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisePage;
